import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, TextField } from "@mui/material";
import SingleActionModal from "components/singleActionModal";
import CategoriesAndSubcategoriesModal from "./categoriesAndSubcategories";
import { makeStyles } from "@mui/styles";
import { useAsyncReducer } from "store/selectTemplate";
import getMachineManufacturers from "api/handlers/machine/getMachineManufacturers";
import getMachineProductLines from "api/handlers/machine/getMachineProductLines";
import { EMachineHierarchyType } from "store/manuals/types";
import { useDispatch } from "react-redux";
import {
  createMachineManufacturer,
  createMachineModelType,
  createMachineProductLine,
} from "store/manuals/actions";
import { useSelector } from "react-redux";
import { AppState } from "store";
import SearchMenu from "components/searchMenu";

interface IProps {
  open: boolean;
  machineHierarchyType?: string | null;
  closeModal: () => void;
}

const useStyles = makeStyles((theme: any) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),

    "& .css-f90lpy-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input.MuiSelect-select":
      {
        paddingTop: "0.6rem",
        paddingBottom: "0.6rem",
      },
  },
  filter: {
    width: "100%",

    "& .MuiSelect-select": {
      paddingTop: "11.5px",
      paddingBottom: "11.5px",
    },
  },
}));

const SelectField = ({
  data,
  value,
  onChange,
  id,
  label,
  placeholder,
  required,
  loading,
}: any) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [searchValue, setSearchValue] = useState("");

  const optionsData = useMemo(() => {
    let options: any = [];
    if (data) {
      options = data.results.map((item: any) => ({
        text: item.name,
        value: item.id,
      }));
    }
    return [
      {
        text: t("manual.machineHierarchy.unassigned"),
        value: null,
      },
      ...options,
    ];
  }, [data]);

  const handleSearch = (val: string) => setSearchValue(val);

  return (
    <>
      <SearchMenu
        id={id}
        label={label}
        options={optionsData}
        value={value}
        onChange={onChange}
        loading={loading}
        onSearch={handleSearch}
        defaultValue={value}
        searchPlaceholder={placeholder}
      />
    </>
  );
};

const CreateMachineHierarchy = memo(
  ({ open, machineHierarchyType, closeModal }: IProps) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const dispatch: any = useDispatch();
    const { modalSubcategory, modalManufacturer } = useSelector(
      (state: AppState) => state.manuals
    );
    const { data: manufacturers, loading: manufacturerLoading } =
      useAsyncReducer(
        machineHierarchyType === EMachineHierarchyType.machineProductLine
          ? "machineManufacturers"
          : undefined,
        getMachineManufacturers,
        {}
      );

    const { data: productLines, loading: productLineLoading } = useAsyncReducer(
      machineHierarchyType === EMachineHierarchyType.machineModelType
        ? "machineProductLines"
        : undefined,
      getMachineProductLines,
      {}
    );

    const [nameValue, setNameValue] = useState<any>(null);
    const [subcategoryValue, setSubcategoryValue] = useState<any>(
      modalSubcategory || null
    );
    const [manufacturerValue, setManufacturerValue] = useState<any>(
      modalManufacturer || null
    );
    const [productLineValue, setProductLineValue] = useState<any>(null);
    const [descriptionValue, setDescriptionValue] = useState<any>(null);

    const onCancel = useCallback(() => {
      closeModal();
      setNameValue(null);
      setSubcategoryValue(modalSubcategory || null);
      setManufacturerValue(modalManufacturer || null);
      setProductLineValue(null);
      setDescriptionValue(null);
    }, [closeModal]);

    const onSubmit = useCallback(() => {
      if (machineHierarchyType === EMachineHierarchyType.machineManufacturer) {
        dispatch(
          createMachineManufacturer({
            name: nameValue,
            description: descriptionValue,
          })
        );
      } else if (
        machineHierarchyType === EMachineHierarchyType.machineModelType
      ) {
        dispatch(
          createMachineModelType({
            name: nameValue,
            description: descriptionValue,
            machine_product_line: productLineValue,
          })
        );
      } else {
        dispatch(
          createMachineProductLine({
            name: nameValue,
            description: descriptionValue,
            machine_manufacturer: manufacturerValue,
            machine_subcategory: subcategoryValue,
          })
        );
      }
      onCancel();
    }, [
      dispatch,
      onCancel,
      nameValue,
      productLineValue,
      descriptionValue,
      manufacturerValue,
      subcategoryValue,
    ]);

    useEffect(() => {
      setSubcategoryValue(modalSubcategory);
    }, [modalSubcategory]);

    useEffect(() => {
      setManufacturerValue(modalManufacturer);
    }, [modalManufacturer]);

    const disabledProductLine =
      !nameValue || !subcategoryValue || !manufacturerValue;
    const disabledModelType = !nameValue || !productLineValue;

    return (
      <SingleActionModal
        open={open}
        modalTitle={t(
          `manualModal.machineHierarchy.create.title.${machineHierarchyType}`
        )}
        finishButtonLabel={t("manualModal.machineHierarchy.create.confirm")}
        closeModal={onCancel}
        handleFinish={onSubmit}
        submitDisabled={
          machineHierarchyType === EMachineHierarchyType.machineProductLine
            ? disabledProductLine
            : machineHierarchyType === EMachineHierarchyType.machineModelType
              ? disabledModelType
              : !nameValue
        }
      >
        <Box className={classes.root}>
          <TextField
            label={t(
              `manualModal.machineHierarchy.${machineHierarchyType}.name`
            )}
            value={nameValue}
            onChange={(e: any) => {
              setNameValue(e.target.value);
            }}
            type="text"
            variant="outlined"
            size="small"
            sx={{
              width: "100%",
            }}
            required
          />
          {machineHierarchyType === EMachineHierarchyType.machineProductLine ? (
            <>
              <CategoriesAndSubcategoriesModal
                value={subcategoryValue}
                onChange={(e: any) => setSubcategoryValue(e.target.value)}
                isCustom={true}
              />
              <SelectField
                data={manufacturers}
                label={t(
                  "manualModal.manualHierarchy.machineManufacturer.label"
                )}
                id="machineHierarchyManufacturer"
                required={true}
                loading={manufacturerLoading}
                value={manufacturerValue}
                onChange={(e: any) => setManufacturerValue(e.target.value)}
              />
            </>
          ) : (
            <></>
          )}
          {machineHierarchyType === EMachineHierarchyType.machineModelType ? (
            <SelectField
              data={productLines}
              label={t("manualModal.manualHierarchy.machineProductLine.label")}
              id="machineHierarchyManufacturer"
              required={true}
              loading={productLineLoading}
              value={productLineValue}
              onChange={(e: any) => setProductLineValue(e.target.value)}
            />
          ) : (
            <></>
          )}
          <TextField
            label={t(
              `manualModal.machineHierarchy.${machineHierarchyType}.description`
            )}
            value={descriptionValue}
            onChange={(e: any) => {
              setDescriptionValue(e.target.value);
            }}
            type="text"
            variant="outlined"
            size="small"
            sx={{
              width: "100%",
            }}
          />
        </Box>
      </SingleActionModal>
    );
  }
);

export default CreateMachineHierarchy;
